import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { priorityColor, statusColor } from "@/lib/api";
import { History, MapPin, Clock, Radio, User, Pencil } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * Read-only incident detail popup opened from the map's Show More button.
 *
 * Shows the complete story of the incident — header, formatted Incident
 * Information line, timestamps, dispatchers/units that touched the call, and
 * the full chronological update log with users + timestamps. No form fields,
 * no action buttons: those live on the side panel's "UPDATE JOB" flow.
 */
export default function IncidentInfoDialog({ incident, open, onOpenChange, onUpdate }) {
  if (!incident) return null;

  const updates = (incident.updates || []).slice().reverse(); // newest first
  // Deduped list of unit ids that paged or updated this call.
  const unitSet = new Set();
  for (const u of incident.updates || []) if (u.unit_id) unitSet.add(u.unit_id);
  const units = Array.from(unitSet);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="incident-info-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-mono tracking-widest text-xl flex items-center gap-3">
                {incident.incident_number}
                <Badge style={{ background: priorityColor(incident.priority), color: "white" }} className="uppercase">
                  {incident.priority}
                </Badge>
                <Badge variant="outline" style={{ borderColor: statusColor(incident.status), color: statusColor(incident.status) }}>
                  {incident.status}
                </Badge>
                {incident.branch && (
                  <Badge variant="outline" className="font-mono uppercase">{incident.branch}</Badge>
                )}
              </DialogTitle>
              <div className="mt-2 text-lg font-semibold" data-testid="info-call-type">{incident.call_type || "—"}</div>
            </div>
            {onUpdate && (
              <Button
                variant="destructive"
                onClick={() => { onOpenChange?.(false); onUpdate(incident); }}
                data-testid="info-update-job-btn"
                className="font-mono tracking-widest"
              >
                <Pencil className="w-4 h-4 mr-1" /> UPDATE JOB
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Incident Information block — STATE | CITY | CALL TYPE | BOX # | ADDRESS | CALL INFO/UPDATE */}
        <div className="border border-border rounded-sm p-3 bg-muted/30 font-mono text-sm">
          <div className="text-[10px] text-muted-foreground tracking-widest mb-1">INCIDENT INFORMATION</div>
          <div className="break-words" data-testid="info-incident-info">
            <span>{incident.state || "—"}</span>
            <span className="text-muted-foreground"> | </span>
            <span>{incident.city || "—"}</span>
            <span className="text-muted-foreground"> | </span>
            <span className="font-semibold">{incident.call_type || "—"}</span>
            <span className="text-muted-foreground"> | </span>
            <span>{incident.box_number ? `Box ${incident.box_number}` : "—"}</span>
            <span className="text-muted-foreground"> | </span>
            <span>{incident.address || "—"}</span>
            <span className="text-muted-foreground"> | </span>
            <span className="text-primary whitespace-pre-wrap">{incident.notes || "—"}</span>
            {incident.page_number != null && (
              <>
                <span className="text-muted-foreground"> | </span>
                <span className="text-amber-500">Page #{incident.page_number}</span>
              </>
            )}
          </div>
        </div>

        {/* Meta grid: timestamps, page count, dispatchers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-xs">
          <div className="border border-border p-2 rounded-sm">
            <div className="text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> RECEIVED</div>
            <div data-testid="info-received-at">{fmtTime(incident.created_at)}</div>
          </div>
          <div className="border border-border p-2 rounded-sm">
            <div className="text-muted-foreground flex items-center gap-1"><Radio className="w-3 h-3" /> LAST PAGED</div>
            <div data-testid="info-last-paged-at">{fmtTime(incident.last_paged_at)}</div>
          </div>
          <div className="border border-border p-2 rounded-sm">
            <div className="text-muted-foreground">PAGES</div>
            <div data-testid="info-page-count">{incident.page_count || 1}</div>
          </div>
          <div className="border border-border p-2 rounded-sm">
            <div className="text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> CREATED BY</div>
            <div data-testid="info-created-by">{incident.created_by_handle || incident.created_by_name || "—"}</div>
          </div>
        </div>

        {(incident.reporting_party || units.length > 0 || incident.notes) && (
          <div className="grid md:grid-cols-2 gap-3 font-mono text-xs">
            {incident.reporting_party && (
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground">REPORTING PARTY</div>
                <div>{incident.reporting_party}</div>
              </div>
            )}
            {units.length > 0 && (
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground">DISPATCHERS / UNITS</div>
                <div data-testid="info-units">{units.join(", ")}</div>
              </div>
            )}
            {incident.notes && (
              <div className="border border-border p-2 rounded-sm md:col-span-2">
                <div className="text-muted-foreground">CURRENT NOTES</div>
                <div className="whitespace-pre-wrap" data-testid="info-current-notes">{incident.notes}</div>
              </div>
            )}
          </div>
        )}

        {(incident.lat && incident.lon) && (
          <div className="font-mono text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {Number(incident.lat).toFixed(5)}, {Number(incident.lon).toFixed(5)}
          </div>
        )}

        <Separator />

        {/* Full update history */}
        <div>
          <div className="font-mono text-xs text-primary mb-2 flex items-center gap-2">
            <History className="w-4 h-4" /> UPDATE HISTORY
          </div>
          <ScrollArea className="h-[320px] pr-2 border border-border/60 rounded-sm p-2">
            <div className="space-y-3" data-testid="info-history-list">
              {updates.length === 0 && <div className="text-muted-foreground text-xs">No updates yet.</div>}
              {updates.map((u, idx) => (
                <div key={u.id || `update-${idx}`} className="border-l-2 border-primary/40 pl-3 py-1">
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {fmtTime(u.timestamp)}
                    {u.user && <> • <span className="text-foreground/80">{u.user}</span></>}
                    {u.unit_id && <> • <span className="text-primary">{u.unit_id}</span></>}
                  </div>
                  <div className="text-sm">
                    <Badge variant="outline" className="mr-2 font-mono text-[10px] uppercase">{(u.type || "update").replace(/_/g, " ")}</Badge>
                    {u.message || "—"}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
